import { useEffect, useState } from "react";
import { Check, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type PhotoSlot = "front" | "left" | "right";
type ProductType = "skin" | "hair";

interface UserData {
  fullName: string;
  phone: string;
  email: string;
}

interface ReportCounterScreenProps {
  onComplete: () => void;
  onViewReport: () => void;
  duration?: number;
  email: string;
  onHome: () => void;
  onReset: () => void;
  userData?: UserData;
  photos?: Record<PhotoSlot, string>;
  hairPhoto?: string;
  hairQuestionnaire?: any;
  productType?: ProductType;
  tenantId?: string;
  locale?: string;
  compressImage?: (dataUrl: string) => Promise<string>;
}

export const ReportCounterScreen = ({ 
  onComplete, 
  onViewReport,
  duration = 30,
  email,
  onHome,
  onReset,
  userData,
  photos,
  hairPhoto,
  hairQuestionnaire,
  productType,
  tenantId,
  locale,
  compressImage
}: ReportCounterScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [isComplete, setIsComplete] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);

  // Start analysis in background
  useEffect(() => {
    if (!analysisStarted && userData && tenantId && locale && compressImage) {
      setAnalysisStarted(true);

      const normalizeHairQuestionnaire = (q: any) => {
        if (!q || typeof q !== "object") return q;
        const toNumberIfPossible = (v: any) => {
          const n = typeof v === "number" ? v : Number(v);
          return Number.isFinite(n) ? n : v;
        };

        return {
          ...q,
          age: toNumberIfPossible(q.age),
          sleepHours: toNumberIfPossible(q.sleepHours),
        };
      };
      
      const runAnalysis = async () => {
        try {
          let requestBody: any = {
            mode: productType,
            locale,
            tenantId,
            fullName: userData.fullName,
            phone: userData.phone,
            email: userData.email,
          };

          if (productType === "hair" && hairPhoto) {
            const compressedPhoto = await compressImage(hairPhoto);
            requestBody.photo = compressedPhoto;
            requestBody.questionnaire = normalizeHairQuestionnaire(hairQuestionnaire);
          } else if (productType === "skin" && photos) {
            const [compressedFront, compressedLeft, compressedRight] = await Promise.all([
              compressImage(photos.front),
              compressImage(photos.left),
              compressImage(photos.right),
            ]);

            requestBody.photos = {
              front: compressedFront,
              left: compressedLeft,
              right: compressedRight,
            };
          }

          console.log("Starting analysis in background...");
          const { data, error } = await supabase.functions.invoke("analyze-face", {
            body: requestBody,
          });

          if (error) {
            console.error("Analysis error:", error);
            toast.error(error.message || "Analysis failed");
            return;
          }

          if (!data?.success) {
            console.error("Analysis failed:", data);
            toast.error(data?.error || "Analysis failed");
            return;
          }

          console.log("Analysis completed successfully");

          const leadPayload = {
            fullName: userData.fullName,
            phone: userData.phone,
            email: userData.email,
            mode: productType === "hair" ? "hair" : "skin",
            tenantId,
          };

          await Promise.allSettled([
            supabase.functions.invoke("send-lead-to-frontdesk", { body: leadPayload }),
            supabase.functions.invoke("send-lead-to-webhook", { body: leadPayload }),
          ]).then((results) => {
            results.forEach((result, index) => {
              const target = index === 0 ? "frontdesk" : "webhook";
              if (result.status === "rejected") {
                console.warn(`${target} notification failed:`, result.reason);
              }
            });
          });
        } catch (error: any) {
          console.error("Analysis error:", error);
          toast.error(error.message || "Analysis failed");
        }
      };

      runAnalysis();
    }
  }, [analysisStarted, userData, photos, hairPhoto, hairQuestionnaire, productType, tenantId, locale, compressImage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsComplete(true);
          return 100;
        }
        return prev + (100 / duration);
      });

      setTimeRemaining(prev => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [duration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col h-full overflow-hidden font-sans bg-[hsl(217,91%,30%)]">
      {/* Background - solid blue to cover everything including safe areas */}
      <div className="fixed inset-0 bg-[hsl(217,91%,30%)]" />
      
      {/* Main Content - No scroll */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-5 min-h-0 pb-safe">
        <div className="w-full max-w-sm space-y-4 text-center">
          
          {/* Circular Progress - Smaller */}
          <div className="relative w-36 h-36 mx-auto">
            <div 
              className="absolute inset-0 rounded-full blur-xl opacity-30"
              style={{
                background: `radial-gradient(circle, hsl(var(--brand-gold)) ${progress}%, transparent 70%)`,
              }}
            />
            
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r="85"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="100"
                cy="100"
                r="85"
                stroke="hsl(var(--brand-gold))"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 85}`}
                strokeDashoffset={`${2 * Math.PI * 85 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-linear"
                style={{ filter: "drop-shadow(0 0 12px hsl(var(--brand-gold) / 0.5))" }}
              />
            </svg>
            
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isComplete ? (
                <div className="bg-gradient-to-br from-[hsl(var(--brand-gold))] to-[hsl(var(--brand-gold)/0.8)] rounded-full p-3 animate-scale-in shadow-lg">
                  <Check className="w-8 h-8 text-white" />
                </div>
              ) : (
                <>
                  <div className="text-3xl font-bold text-white tracking-tight">
                    {formatTime(timeRemaining)}
                  </div>
                  <div className="text-sm text-white/60 font-medium">
                    {Math.round(progress)}%
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status text - Compact */}
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-white tracking-tight">
              {isComplete ? "Analysis Complete!" : "Generating Report"}
            </h1>
            <p className="text-sm text-white/70">
              {isComplete
                ? `Your ${productType === "hair" ? "hair" : "skin"} report is ready`
                : `Finalizing your ${productType === "hair" ? "hair" : "skin"} report...`
              }
            </p>
            {!isComplete && (
              <div className="flex items-center justify-center gap-1.5 pt-1">
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand-gold))] animate-pulse" />
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand-gold))] animate-pulse" style={{ animationDelay: '0.15s' }} />
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--brand-gold))] animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
            )}
          </div>

          {/* Result Card - Only show when complete - Compact */}
          {isComplete && (
            <div className="space-y-3 animate-fade-in">
              <div className="bg-white rounded-xl p-4 shadow-xl text-left">
                <div className="text-center mb-3 pb-3 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-[hsl(217,91%,25%)]">Report Sent!</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Check your inbox</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1.5 bg-gray-50 rounded-lg px-2.5">
                    <span className="text-sm text-gray-600">Status</span>
                    <span className="text-sm font-semibold text-success">Complete</span>
                  </div>
                  <div className="py-1.5 bg-[hsl(217,91%,97%)] rounded-lg px-2.5">
                    <span className="text-xs text-gray-500">Sent to:</span>
                    <p className="text-sm font-semibold text-[hsl(217,91%,35%)] break-all">{email}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Compact */}
              <div className="space-y-2">
                <Button
                  onClick={onHome}
                  variant="outline"
                  className="w-full h-11 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-xl font-bold text-base"
                >
                  <Home className="w-4 h-4 mr-1.5" />
                  Return Home
                </Button>
                <Button
                  onClick={onReset}
                  className="w-full h-11 bg-white text-[hsl(217,91%,30%)] hover:bg-white/90 rounded-xl font-bold text-base shadow-lg"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  New Analysis
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
